import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, keyframes } from '@mui/material';
import { HourglassTop as HourglassIcon, Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios, { API, clearAuth, getToken } from '../api';

const pulse = keyframes`0%,100% { opacity: 1; } 50% { opacity: 0.45; }`;

export default function Pending() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(localStorage.getItem('userStatus') || 'pending');
  const email = localStorage.getItem('userEmail') || '';

  const check = useCallback(async () => {
    if (!getToken()) { navigate('/login'); return; }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      localStorage.setItem('userStatus', data.status || 'pending');
      localStorage.setItem('userRole', data.role || 'user');
      setStatus(data.status);
      if (data.status === 'active') navigate('/');
    } catch (e) { /* interceptor handles 401 */ }
  }, [navigate]);

  useEffect(() => {
    check();
    const id = setInterval(check, 12000); // re-vérifie toutes les 12s
    return () => clearInterval(id);
  }, [check]);

  const logout = async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch (e) {}
    clearAuth();
    navigate('/login');
  };

  const suspended = status === 'suspended';
  const expired = status === 'expired';

  const title = suspended ? 'Compte suspendu' : expired ? 'Abonnement expiré' : 'En attente de validation';
  const message = suspended
    ? "Ton accès a été suspendu par un administrateur. Contacte le support si tu penses que c'est une erreur."
    : expired
      ? "Ton abonnement est arrivé à échéance. Un administrateur doit le renouveler pour réactiver ton accès."
      : "Ton compte a bien été créé ✅ Un administrateur doit valider ton accès au SaaS. Cette page se mettra à jour automatiquement dès que c'est fait.";

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#09090b',
      backgroundImage: 'radial-gradient(60% 50% at 50% 0%, rgba(99,102,241,0.12), transparent 70%)',
      p: 2,
    }}>
      <Paper elevation={0} sx={{
        maxWidth: 460, width: '100%', p: 4.5, textAlign: 'center',
        bgcolor: '#101012', border: '1px solid #27272a', borderRadius: 4,
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
      }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: suspended || expired ? 'rgba(239,68,68,0.14)' : 'rgba(99,102,241,0.14)',
          color: suspended || expired ? '#f87171' : '#818cf8',
          animation: suspended || expired ? 'none' : `${pulse} 2s ease-in-out infinite`,
        }}>
          {suspended || expired ? <LockIcon sx={{ fontSize: 30 }} /> : <HourglassIcon sx={{ fontSize: 30 }} />}
        </Box>

        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#fafafa', mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.92rem', color: '#a1a1aa', lineHeight: 1.6, mb: 3 }}>{message}</Typography>

        {!suspended && !expired && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 3, color: '#71717a' }}>
            <CircularProgress size={15} sx={{ color: '#818cf8' }} />
            <Typography sx={{ fontSize: '0.82rem' }}>En attente de l'approbation…</Typography>
          </Box>
        )}

        {email && (
          <Typography sx={{ fontSize: '0.78rem', color: '#71717a', mb: 2.5 }}>
            Connecté en tant que <b style={{ color: '#a1a1aa' }}>{email}</b>
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={check} sx={{ borderColor: '#27272a', color: '#fafafa' }}>
            Vérifier maintenant
          </Button>
          <Button variant="text" onClick={logout} sx={{ color: '#a1a1aa' }}>Se déconnecter</Button>
        </Box>
      </Paper>
    </Box>
  );
}
